import React, { FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useStoreContext } from 'features/store';
import { useSortStores } from 'features/store/hooks/use-sort-stores';
import { useSearching } from 'features/store/pages/pickup-store-selector/hooks/use-searching';
import { orderBy, uniqBy } from 'lodash-es';

import { IRestaurant } from '@rbi-ctg/store';
import { calculateDistance } from 'hooks/geolocation/use-distance';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import { ILocation } from 'hooks/use-map/types';
import { usePrevious } from 'hooks/use-previous';
import { useAuthContext } from 'state/auth';
import { useGeolocation } from 'state/geolocation';
import { t } from 'state/intl/translations';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import { useScrollViewFullHeight } from 'state/scroll/hooks';
import { isValidPosition } from 'utils/geolocation';
import { TraceName, startTrace } from 'utils/performance/trace';

import { DismissPrompt } from './components';
import {
  FocusedBy,
  MapViewRef,
  StoreCardListRef,
  StoreLocatorActionType,
  StoreLocatorActions,
} from './components/types';
import { IStore, mapStoresResponse, searchNearbySuccess } from './ducks/stores';
import { useHighlightStore } from './hooks/use-highlight-store';
import { useSearchRestaurants } from './hooks/use-search-restaurants';
import { useStoreLocatorDialogs } from './hooks/use-store-locator-dialogs';
import { useStoreLocatorHandlers } from './hooks/use-store-locator-handlers';
import { useStoreLocatorInit } from './hooks/use-store-locator-init';
import useStoreLocatorTabs from './hooks/use-store-locator-tabs';
import { useStoresReducer } from './hooks/use-stores-reducer';
import { StoreLocatorView } from './store-locator.view';

/**
 *
 * StoreLocator purpose
 *
 */
const RESTAURANT_LIMIT = 10;

const useStoreLocatorRefs = () => {
  const mapRef = useRef<MapViewRef>(null);
  const flatListRef = useRef<StoreCardListRef>(null);
  const actions = useRef<StoreLocatorActions>({
    [StoreLocatorActionType.INITIAL_LOAD]: false,
    [StoreLocatorActionType.LOCATION_RESET]: false,
    [StoreLocatorActionType.MANUAL_SEARCH]: false,
    [StoreLocatorActionType.AREA_SEARCH]: false,
    [StoreLocatorActionType.USER_SELECTION]: false,
  });

  return { mapRef, flatListRef, actions };
};

const useScrollHandlers = (listRef: RefObject<StoreCardListRef | null>) => {
  const scrollToRestaurant = (restaurantId: string) =>
    listRef?.current?.scrollToRestaurant(restaurantId);

  const scrollToTop = () => listRef?.current?.scrollToTop();

  return {
    scrollToRestaurant,
    scrollToTop,
  };
};

const useSortStoresByTab = ({
  manualCoordinates,
  isPermissionGranted,
  userCoordinates,
  lastUserCoordinateTimestamp,
  focusedByRef,
  focusedRestaurant,
  storesNearby,
  storesRecent,
  storesFavs,
  activeStore,
  cachedStoresNearby,
}: {
  manualCoordinates: ILocation | null;
  isPermissionGranted: boolean;
  userCoordinates: ILocation | null;
  lastUserCoordinateTimestamp: number;
  focusedByRef: React.MutableRefObject<FocusedBy>;
  focusedRestaurant: IStore | null;
  storesNearby: IStore[];
  storesRecent: IStore[];
  storesFavs: IStore[];
  activeStore: IStore | null;
  cachedStoresNearby: IStore[];
}) => {
  const getHighlightedAndRegularStores = useCallback(
    (restaurants: IStore[], focusedRestaurant?: IStore | null): [IStore | undefined, IStore[]] => {
      // Only process if the focused store is from the map
      const coords = manualCoordinates ?? (isPermissionGranted ? userCoordinates : null);

      if (!focusedRestaurant || focusedByRef.current === FocusedBy.LIST) {
        return [undefined, restaurants];
      }

      // Add distance to focused restaurant
      const focusedWithDistance = {
        ...focusedRestaurant,
        distance: calculateDistance(
          { lat: focusedRestaurant.latitude ?? 0, lng: focusedRestaurant.longitude ?? 0 },
          coords,
          userCoordinates,
          lastUserCoordinateTimestamp,
          true
        ),
      };

      // Filter out the focused restaurant from regular list
      const regularRestaurants = restaurants.filter(
        restaurant => restaurant._id !== focusedRestaurant?._id
      );

      return [focusedWithDistance, regularRestaurants];
    },
    [
      focusedByRef,
      isPermissionGranted,
      lastUserCoordinateTimestamp,
      manualCoordinates,
      userCoordinates,
    ]
  );

  // Process all stores and order them by distance, focused store first and then regular stores are ordered by distance and isAvailable
  const sortStoresByDistance = useCallback(
    (restaurants: IStore[]): IStore[] => {
      if (!restaurants.length) {
        return [];
      }

      const highLightedStore = restaurants.find(
        restaurant => restaurant._id === focusedRestaurant?._id
      );

      // Focused store go to the top of the list
      const [highlightedStore, regularsStores] = getHighlightedAndRegularStores(
        restaurants,
        highLightedStore
      );
      // Sort regular stores
      const sortedRegular = orderBy(regularsStores, ['isAvailable', 'distance'], ['desc', 'asc']);

      return highlightedStore ? [highlightedStore, ...sortedRegular] : sortedRegular;
    },
    [getHighlightedAndRegularStores, focusedRestaurant?._id]
  );

  const storesByTab: {
    [K in OrderLocatorTab]: IStore[];
  } = useMemo(() => {
    // Active store is always included in the list since the query could not return it if the user interact with the map
    const nearbyStores = uniqBy(
      activeStore
        ? [...(storesNearby ?? []), ...cachedStoresNearby, activeStore]
        : [...(cachedStoresNearby ?? []), ...(storesNearby ?? [])],
      '_id'
    ).filter(Boolean);

    return {
      [OrderLocatorTab.NEARBY]: sortStoresByDistance(nearbyStores),
      [OrderLocatorTab.RECENT]: sortStoresByDistance(storesRecent ?? []),
      [OrderLocatorTab.FAVORITE]: sortStoresByDistance(storesFavs ?? []),
      [OrderLocatorTab.DELIVERY]: [],
    };
  }, [
    sortStoresByDistance,
    storesNearby,
    activeStore,
    storesRecent,
    storesFavs,
    cachedStoresNearby,
  ]);

  return storesByTab;
};

export const PickupStoreSelector: FC<React.PropsWithChildren<{
  isInSupportForm?: boolean;
  onSelectStore?: (storeId: string) => void;
  onDismissInSupportForm?: () => void;
}>> = ({ isInSupportForm, onSelectStore, onDismissInSupportForm, ...rest }) => {
  const { mapRef, flatListRef, actions } = useStoreLocatorRefs();

  useScrollViewFullHeight();
  useRumPageView('store-locator', 'Store Locator');
  const {
    onDismissCallback,
    dismissPromptOpen,
    handleCloseDismissPrompt,
  } = useStoreLocatorDialogs({ onDismissInSupportForm });
  const [cachedStoresNearby, setCachedStoresNearby] = useState<IStore[]>([]);
  const { scrollToRestaurant, scrollToTop } = useScrollHandlers(flatListRef);
  const sortRestaurantsByAvailability = useSortStores();
  const {
    activeCoordinates,
    manualCoordinates,
    userCoordinates,
    setSearchThisAreaCoordinates,
    isPermissionGranted,
    lastUserCoordinateTimestamp,
    setManualAddress,
    resetCoordinatesManual,
    updateManualCoordinates,
    loadCurrentPositionAsync,
  } = useGeolocation();

  const { user } = useAuthContext();
  const { StoreChangeDialog, store: activeRestaurant } = useStoreContext();

  // perf tracing
  useEffect(() => {
    if (activeRestaurant) {
      startTrace(TraceName.STORE_LOCATOR_RESTAURANT_ALREADY_SELECTED_TO_LIST_ITEM_DISPLAYED);
      return;
    }

    if (isPermissionGranted) {
      startTrace(TraceName.STORE_LOCATOR_AUTO_NEARBY_LOCATION_SEARCH_TO_LIST_ITEM_DISPLAYED);
      return;
    }
  }, []);

  const prevUserId = usePrevious(user?.cognitoId);

  const {
    errorFavs,
    errorNearby,
    errorRecent,
    searchingNearby,
    searchingRecent,
    searchingFavs,
    storesNearby,
    storesFavs,
    storesRecent,
    rawIsSearching,
    storesById,
    storeLocatorDispatch,
  } = useStoresReducer();
  const combinedStoresById = useMemo(() => {
    return activeRestaurant?._id
      ? { [activeRestaurant._id]: activeRestaurant, ...storesById }
      : { ...storesById };
  }, [activeRestaurant, storesById]);

  const { isSearchingFavorites, isSearchingNearby, isSearchingRecent } = useSearching({
    searchingNearby,
    searchingRecent,
    searchingFavs,
    rawIsSearching,
  });

  const {
    ErrorDialog,
    Dialog,
    searchFavRestaurants,
    searchNearbyRestaurants,
    searchRecentRestaurants,
    searchNearbyRestaurantsAsync,
  } = useSearchRestaurants({
    storeLocatorDispatch,
  });

  const fetchAndSortNearbyRestaurantsAsync = useCallback(
    async (location: ILocation): Promise<IStore[]> => {
      const stores = (await searchNearbyRestaurantsAsync({
        location,
        first: RESTAURANT_LIMIT,
      })) as IRestaurant[] | undefined;
      const sortedStores = sortRestaurantsByAvailability(stores ?? []);
      storeLocatorDispatch(searchNearbySuccess(sortedStores));
      const mappedStores = mapStoresResponse([...sortedStores]);
      return mappedStores;
    },
    [searchNearbyRestaurantsAsync, sortRestaurantsByAvailability, storeLocatorDispatch]
  );

  const closestStore = storesNearby?.[0] ?? null;
  const previousClosestStore = usePrevious(closestStore) ?? null;
  const activeStore: IStore | null = useMemo(() => {
    return activeRestaurant?._id
      ? {
          ...activeRestaurant,
          country: activeRestaurant?.physicalAddress?.country ?? '',
          address: activeRestaurant?.physicalAddress?.address1 ?? '',
        }
      : null;
  }, [activeRestaurant]);

  const allStores = useMemo(
    () =>
      uniqBy(
        [
          activeStore,
          ...(cachedStoresNearby ?? []),
          ...(storesNearby ?? []),
          ...(storesRecent ?? []),
          ...(storesFavs ?? []),
        ],
        '_id'
      ).filter((store): store is IStore => Boolean(store)),

    [activeStore, storesNearby, cachedStoresNearby, storesRecent, storesFavs]
  );

  const {
    currentTabHasError,
    currentTabIndex,
    currentTabKey,
    handleTabChange,
    locatorTabs,
  } = useStoreLocatorTabs({ errorFavs, errorNearby, errorRecent });

  const { focusedRestaurant, focusedByRef, handleOnRestaurantHighLight } = useHighlightStore({
    storesRecent: storesRecent ?? [],
    storesFavs: storesFavs ?? [],
    storesNearby: storesNearby ?? [],
    onTabChange: handleTabChange,
    onScrollToRestaurant: scrollToRestaurant,
    onScrollToTop: scrollToTop,
    mapRef,
    actions,
    closestStore,
    previousClosestStore,
    activeStore,
    userCoordinates,
  });

  const handleSearchAllStores = useCallback(
    (location?: ILocation | null) => {
      const coordinates = location ?? activeCoordinates;
      if (isValidPosition(coordinates)) {
        searchNearbyRestaurants({
          location: coordinates,
          first: RESTAURANT_LIMIT,
        });
      }
      if (user?.cognitoId) {
        searchFavRestaurants(coordinates);
        searchRecentRestaurants(coordinates, RESTAURANT_LIMIT);
      }
    },
    [
      activeCoordinates,
      searchFavRestaurants,
      searchNearbyRestaurants,
      searchRecentRestaurants,
      user?.cognitoId,
    ]
  );

  const {
    handleSearchThisArea,
    handleResetUserCoords,
    handleResetManualSearch,
    handleAddressSelected,
  } = useStoreLocatorHandlers({
    actions,
    setSearchThisAreaCoordinates,
    fetchAndSortNearbyRestaurantsAsync,
    highlightStore: handleOnRestaurantHighLight,
    setManualAddress,
    isPermissionGranted,
    resetCoordinatesManual,
    mapRef,
    cachedStoresNearby,
    userCoordinates,
    loadCurrentPositionAsync,
    flatListRef,
    updateManualCoordinates,
  });

  const storesByTab = useSortStoresByTab({
    manualCoordinates,
    isPermissionGranted,
    userCoordinates,
    lastUserCoordinateTimestamp: lastUserCoordinateTimestamp ?? 0,
    focusedByRef: focusedByRef as React.MutableRefObject<FocusedBy>,
    focusedRestaurant,
    storesNearby: storesNearby ?? [],
    storesRecent: storesRecent ?? [],
    storesFavs: storesFavs ?? [],
    activeStore,
    cachedStoresNearby,
  });

  const handleOnFavoriteClick = () => {
    searchFavRestaurants(activeCoordinates);
  };

  // Get user's favorite and recent stores when user changes
  useEffect(() => {
    if (prevUserId !== user?.cognitoId && isValidPosition(activeCoordinates)) {
      searchFavRestaurants(activeCoordinates);
      searchRecentRestaurants(activeCoordinates, RESTAURANT_LIMIT);
    }
  }, [
    activeCoordinates,
    prevUserId,
    searchFavRestaurants,
    searchRecentRestaurants,
    user?.cognitoId,
  ]);

  // Cache stores nearby
  useEffect(() => {
    if (storesNearby?.length) {
      setCachedStoresNearby(prev => {
        const newStores = storesNearby.filter(
          store => !prev.some(cached => cached._id === store._id)
        );
        if (newStores.length === 0) {
          return prev;
        }
        return [...prev, ...newStores];
      });
    }
  }, [setCachedStoresNearby, storesNearby]);

  // Initialize the store locator
  useStoreLocatorInit({
    mapRef,
    activeStore,
    nearbyStore: closestStore ?? null,
    onRestaurantHighlight: handleOnRestaurantHighLight,
    actions,
    onSearchAllStores: handleSearchAllStores,
    onSearchNearbyAsync: fetchAndSortNearbyRestaurantsAsync,
  });

  useEffectOnUnmount(() => {
    setCachedStoresNearby([]);
    setSearchThisAreaCoordinates(null);
  });

  return (
    <>
      <StoreLocatorView
        storesByTab={storesByTab}
        storesById={combinedStoresById}
        mapRef={mapRef}
        closestStore={closestStore}
        searchingNearby={isSearchingNearby}
        searchingRecent={isSearchingRecent}
        searchingFavs={isSearchingFavorites}
        rawIsSearching={rawIsSearching}
        onSelectStore={onSelectStore}
        isInSupportForm={isInSupportForm}
        onDismissCallback={onDismissCallback}
        actions={actions}
        onFavoriteClick={handleOnFavoriteClick}
        onResetUserCoords={handleResetUserCoords}
        onResetManualSearch={handleResetManualSearch}
        onSearchThisArea={handleSearchThisArea}
        currentTabKey={currentTabKey}
        onTabChange={handleTabChange}
        currentTabIndex={currentTabIndex}
        currentTabHasError={currentTabHasError}
        focusedRestaurant={focusedRestaurant}
        allStores={allStores}
        onRestaurantHighlight={handleOnRestaurantHighLight}
        locatorTabs={locatorTabs}
        activeStore={activeStore}
        flatListRef={flatListRef}
        onAddressSelected={handleAddressSelected}
        {...rest}
      />
      <ErrorDialog />
      <DismissPrompt isOpen={dismissPromptOpen} onClose={handleCloseDismissPrompt} />
      <Dialog />
      <StoreChangeDialog heading={t('changeStores')} />
    </>
  );
};

export default PickupStoreSelector;
